import { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { Button, Space } from "react_reusable_components";
import {
  addCheckedKeysToProduct,
  decryptAuthData,
  getCheckedProductsForEdit,
  getLocalizedMessage,
  getOperationAudit,
  getRepairAudit,
  getValidationObject,
} from "../../UtilityFunctions/utils";

import { TwoRow } from "../../core/layouts";
import { useIntl } from "react-intl";
import AddUserFormContainer from "../AddUserFormContainer";
import {
  addUserFormFieldNames,
  addUserFormInitialValues,
  validationSchema,
} from "../../validationSchema/addUserManagementSchema";
import UserManagementService from "../../services/user-management-service";
import { userRole } from "../../constants";
import ProductCategoryService from "../../services/product-category-service";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import { UserContext } from "../../globalContext/Users/userProvider";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import variables from "../../themes/base/styles/variables";
import Style from "../AddProductCategoryFormContainer/AddProductCategoryForm.module.scss";
import { getUserById as getUserByIdAction } from "../../globalContext/Users/userActions";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { setUser } from "../../globalContext/auth/authActions";
import { StorageService } from "../../services";
import { changePasswordFieldNames } from "../../validationSchema/changePasswordSchema";

export const AddUserManagementFormContainerStyle = {
  mainThreeRowStyle: {
    padding: `${variables.paddingMedium} 0`,
    position: "relative",
  },
  topSectionStyle: {
    padding: `0 ${variables.paddingMedium}`,
    flex: 1,
  },
  bottomSectionStyle: {
    marginTop: variables.marginMediumLarge2,
    display: variables.dFlex,
    justifyContent: "end",
    bottom: variables.paddingMedium,
    right: variables.paddingMedium,
    background: variables.bodyBg,
    paddingRight: variables.paddingMedium,
  },
};

export default function AddUserManagementFormContainer({ isEditMode }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const [userState, userDispatch] = useContext(UserContext);
  const [, authDispatch] = useContext(AuthContext);
  const [disabled, setDisabled] = useState(false);

  const [productSubProductsOptions, setProductSubProductsOptions] = useState(
    []
  );
  const [
    selectedProductAndSubProductOptionsDNSD,
    setSelectedProductAndSubProductOptionsDNSD,
  ] = useState([]);
  const [
    selectedProductAndSubProductOptionsTM,
    setSelectedProductAndSubProductOptionsTM,
  ] = useState([]);
  const [selectedImage, setSelectedImage] = useState("");
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [isOperationAuditForTM, setIsOperationAuditForTM] = useState(false);
  const [isRepairAuditForTM, setIsRepairAuditForTM] = useState(false);
  const [isOperationAuditForDNSD, setIsOperationAuditForDNSD] = useState(false);
  const [isRepairAuditForDNSD, setIsRepairAuditForDNSD] = useState(false);
  const [additionalEmailError, setAdditionalEmailError] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [countryCodeValue, setCountryCodeValue] = useState("");
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [countryId, setCountryId] = useState("");
  const [countriesList, setCountriesList] = useState([]);
  const [createUserData, setCreateUserData] = useState({});
  const loaderDispatch = useContext(GlobalLoaderContext)[1];

  const filterCheckedProducts = useCallback((data) => {
    const newArray = [];
    const myfilteredAray = data
      .filter((product) => product.checked)
      .map((product) => {
        const filteredSubCategory = product.subCategory.filter(
          (sub) => sub.checked
        );
        return { ...product, subCategory: filteredSubCategory };
      });

    myfilteredAray.forEach((product) => {
      product.subCategory.forEach((sub) => {
        newArray.push({
          productCategoryId: product.productCategoryId,
          productSubCategoryId: sub.id,
        });
      });
    });

    return newArray;
  }, []);

  const createSubProductsArrayForCA = useCallback((data) => {
    let newArray = [];
    data.forEach((item) => {
      item.subCategory.forEach((subItem) => {
        newArray.push({
          productSubCategoryId: subItem.id,
          productCategoryId: item.productCategoryId,
        });
      });
    });
    return newArray;
  }, []);

  const getAuditValues = useCallback((roles, roleName, key, callback) => {
    const roleObject = roles?.find((role) => role?.name === roleName);
    if (!roleObject) {
      return;
    }
    const auditValue = roleObject[key];
    callback(auditValue);
  }, []);

  const createPostData = (values) => {
    const isCARoleSelected = values?.rolesId?.includes(userRole.CA);
    const isTMRoleSelected = values?.rolesId?.includes(userRole.TM);
    const isDNSDRoleSelected = values?.rolesId?.includes(userRole.DNSD);
    const onlyMainAdminRoleSelected =
      values?.rolesId?.length === 1 &&
      values?.rolesId?.includes(userRole.ADMIN);
    const subProductCategoryForDNSD = filterCheckedProducts(
      selectedProductAndSubProductOptionsDNSD
    );
    const subProductCategoryForTM = filterCheckedProducts(
      selectedProductAndSubProductOptionsTM
    );
    const subProductCategoryForCA = createSubProductsArrayForCA(
      productSubProductsOptions
    );

    return {
      ...values,
      mobileNumber: values[addUserFormFieldNames.MOB_NUM],
      roles: values.rolesId.map((role) => ({
        roleId: role,
        isOperationsAudit: getOperationAudit({ role, isOperationAuditForTM }),
        isRepairAudit: getRepairAudit({
          role,
          isRepairAuditForDNSD,
          isRepairAuditForTM,
        }),
      })),
      ...(values[addUserFormFieldNames.MOB_NUM] && {
        mobileCountryCode: `+${values[
          addUserFormFieldNames.COUNTRY_CODE
        ]?.substr(1)}`,
      }),
      ...(!onlyMainAdminRoleSelected &&
        (isRepairAuditForDNSD || isRepairAuditForTM || isCARoleSelected) && {
          productSubCategory: [
            ...(isRepairAuditForDNSD
              ? [
                  {
                    roleId: userRole.DNSD,
                    associatedProductCategory: subProductCategoryForDNSD,
                  },
                ]
              : []),
            ...(isRepairAuditForTM
              ? [
                  {
                    roleId: userRole.TM,
                    associatedProductCategory: subProductCategoryForTM,
                  },
                ]
              : []),
            ...(isCARoleSelected
              ? [
                  {
                    roleId: userRole.CA,
                    associatedProductCategory: subProductCategoryForCA,
                  },
                ]
              : []),
          ],
        }),
      ...(imageUrl && isTMRoleSelected && { imageId: imageUrl }),
      ...(isDNSDRoleSelected &&
        additionalEmails?.length > 0 && {
          otherEmail: additionalEmails?.map((email) => email.value),
        }),
    };
  };

  const removeUnnecessaryData = (postData, values) => {
    const onlyMainAdminRoleSelected =
      values?.rolesId?.length === 1 &&
      values?.rolesId?.includes(userRole.ADMIN);
    const onlyDNSDRoleSelected =
      values?.rolesId?.length === 1 && values?.rolesId?.includes(userRole.DNSD);
    const isDNSDRoleSelected = values?.rolesId?.includes(userRole.DNSD);

    if (onlyMainAdminRoleSelected) {
      delete postData.countryId;
    }
    if (values?.mobileNumber?.length === 0) {
      delete postData.mobileNumber;
    }
    if (onlyDNSDRoleSelected) {
      delete postData.employeeId;
    }
    if (!isDNSDRoleSelected) {
      delete postData.globalId;
    }
    if (!values?.rolesId?.includes(userRole.DNSD)) {
      delete postData.address;
    }
    postData.productSubCategory = postData?.productSubCategory?.filter(
      (item) => item?.associatedProductCategory?.length > 0
    );

    return postData;
  };

  const {
    values,
    handleChange,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    resetForm,
    setFieldError,
    handleBlur,
    setValues,
    validateField,
    setFieldTouched,
  } = useFormik({
    initialValues: addUserFormInitialValues,
    onSubmit: async () => {
      const selectedCountryInMobileField = countriesList?.find(
        (country) =>
          country?.code === values[addUserFormFieldNames.COUNTRY_CODE]
      );
      const isSameCountrySelected =
        selectedCountryInMobileField?.id ===
        values[addUserFormFieldNames.COUNTRY_ID];
      const onlyMainAdminRoleSelected =
        values?.rolesId?.length === 1 &&
        values?.rolesId?.includes(userRole.ADMIN);

      let postData = createPostData(values);
      postData = removeUnnecessaryData(postData, values);

      const { rolesId, countryCode, ...restPostData } = postData;

      if (
        additionalEmailError?.length === 0 &&
        (isSameCountrySelected ||
          onlyMainAdminRoleSelected ||
          values[addUserFormFieldNames.COUNTRY_CODE] === "" ||
          values[addUserFormFieldNames.MOB_NUM] === "")
      ) {
        addUserHandler(restPostData);
      } else {
        setCreateUserData(restPostData);
        setOpenConfirmPopup(true);
      }
    },
    validationSchema: validationSchema(
      intl,
      getValidationObject(countriesList)
    ),
  });

  useEffect(() => {
    if (countryId) {
      let queryParams = { countryId: countryId };

      UserManagementService.getSubProductCategoryBasedOnCountry({
        loaderDispatch,
        queryParams,
        intl,
      }).then((data) => {
        setProductSubProductsOptions(data?.result);
      });
    }
  }, [countryId, userState?.userData]);

  useEffect(() => {
    ProductCategoryService.getCountries(intl).then((data) => {
      setCountriesList(data?.result);
    });
  }, [intl]);

  useEffect(() => {
    if (!userId) return;
    UserManagementService.getUserDataById({
      loaderDispatch,
      dispatch: userDispatch,
      intl,
      userId,
    });
    return () => userDispatch(getUserByIdAction({}));
  }, [userId]);

  useEffect(() => {
    if (isEditMode) {
      setCountryId(userState?.userData?.country?.id);
      setValues((prev) => ({
        ...prev,
        [addUserFormFieldNames.NAME]: userState?.userData?.name,
        [addUserFormFieldNames.EMAIL]: userState?.userData?.email,
        [addUserFormFieldNames.EMP_ID]: userState.userData?.employeeId,
        [addUserFormFieldNames.ADDRESS]: userState?.userData?.address,

        ...(userState?.userData?.mobile?.number && {
          [addUserFormFieldNames.MOB_NUM]: userState.userData?.mobile?.number,
          [addUserFormFieldNames.COUNTRY_CODE]:
            userState?.userData?.mobile?.countryCode,
        }),

        [addUserFormFieldNames.ROLES_ID]: userState?.userData?.roles?.map?.(
          ({ id }) => id
        ),
        [addUserFormFieldNames.COUNTRY_ID]: userState?.userData?.country?.id,
        [addUserFormFieldNames.GLOBAL_ID]: userState.userData?.globalId,
      }));

      setAdditionalEmails(
        userState?.userData?.otherEmail?.map?.((value) => ({ value })) || []
      );
      setImageUrl(userState?.userData?.imageId);

      getAuditValues(
        userState?.userData?.roles,
        "TM",
        "isOperationsAudit",
        setIsOperationAuditForTM
      );
      getAuditValues(
        userState?.userData?.roles,
        "TM",
        "isRepairAudit",
        setIsRepairAuditForTM
      );
      getAuditValues(
        userState?.userData?.roles,
        "DNSD",
        "isRepairAudit",
        setIsRepairAuditForDNSD
      );
    }
  }, [isEditMode, userState?.userData, getAuditValues]);

  useEffect(() => {
    if (!isEditMode) return;
    const source = userState?.userData?.userProductCategory;
    if (source?.length > 0) {
      source?.map((sourceItem) => {
        if (sourceItem.roleName === "DNSD") {
          const dnsdCategories = sourceItem.category;
          setSelectedProductAndSubProductOptionsDNSD(
            getCheckedProductsForEdit(productSubProductsOptions, dnsdCategories)
          );
        }
        if (sourceItem.roleName === "TM") {
          const tmCategories = sourceItem.category;
          setSelectedProductAndSubProductOptionsTM(
            getCheckedProductsForEdit(productSubProductsOptions, tmCategories)
          );
        }

        return sourceItem;
      });
    }
  }, [productSubProductsOptions]);

  function roleExists(arr, name) {
    return arr?.some(function (el) {
      return el.name === name;
    });
  }

  useEffect(() => {
    if (isEditMode) {
      if (values?.countryId) {
        if (
          !userState?.userData?.userProductCategory
            .map(({ roleName }) => roleName)
            .includes("DNSD") ||
          !roleExists(userState?.userData.roles, "DNSD") ||
          !userState?.userData?.roles?.find((role) => role.name === "DNSD")
            ?.isRepairAudit
        ) {
          setSelectedProductAndSubProductOptionsDNSD(
            addCheckedKeysToProduct(productSubProductsOptions)
          );
        }

        if (
          !userState?.userData?.userProductCategory
            .map(({ roleName }) => roleName)
            .includes("TM") ||
          !roleExists(userState?.userData.roles, "TM") ||
          !userState?.userData?.roles?.find((role) => role.name === "TM")
            ?.isRepairAudit
        ) {
          setSelectedProductAndSubProductOptionsTM(
            addCheckedKeysToProduct(productSubProductsOptions)
          );
        }
      }
    }
  }, [isEditMode, productSubProductsOptions]);

  useEffect(() => {
    if (!isEditMode) {
      setSelectedProductAndSubProductOptionsDNSD(
        addCheckedKeysToProduct(productSubProductsOptions)
      );
      setSelectedProductAndSubProductOptionsTM(
        addCheckedKeysToProduct(productSubProductsOptions)
      );
    }
  }, [isEditMode, productSubProductsOptions]);

  const addUserHandler = useCallback(
    async (restPostData) => {
      setDisabled(true);
      if (!isEditMode) {
        return UserManagementService.addUser(
          loaderDispatch,
          restPostData,
          navigate,
          intl,
          resetForm
        )
          .then(() => {
            setDisabled(false);
          })
          .catch(() => {
            setDisabled(false);
          })
          .finally(() => {
            setDisabled(false);
            setOpenConfirmPopup(false);
          });
      }

      return UserManagementService.editUser({
        dispatch: userDispatch,
        loaderDispatch,
        body: restPostData,
        navigate,
        intl,
        resetForm,
        userId,
      })
        .then((res) => {
          setDisabled(false);
          StorageService.set("token", {
            token: res?.headers?.authorization,
          });
          const userTokenData = decryptAuthData(res?.headers?.authorization);
          authDispatch(setUser(userTokenData));
          navigate("/users");
          resetForm();
        })
        .catch(() => {
          setDisabled(false);
        })
        .finally(() => {
          setDisabled(false);
          setOpenConfirmPopup(false);
        });
    },
    [intl, isEditMode, loaderDispatch, navigate, resetForm, userId]
  );

  const handleCancel = useCallback(() => {
    resetForm();
    navigate("/users");
  }, [navigate, resetForm]);

  return (
    <>
      <TwoRow
        style={AddUserManagementFormContainerStyle.mainThreeRowStyle}
        topSection={
          <AddUserFormContainer
            handleChange={handleChange}
            values={values}
            errors={errors}
            setFieldValue={setFieldValue}
            setCountryId={setCountryId}
            productSubProductsOptions={productSubProductsOptions}
            setSelectedProductAndSubProductOptionsDNSD={
              setSelectedProductAndSubProductOptionsDNSD
            }
            setSelectedProductAndSubProductOptionsTM={
              setSelectedProductAndSubProductOptionsTM
            }
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            additionalEmails={additionalEmails}
            setAdditionalEmails={setAdditionalEmails}
            isOperationAuditForDNSD={isOperationAuditForDNSD}
            isRepairAuditForDNSD={isRepairAuditForDNSD}
            isOperationAuditForTM={isOperationAuditForTM}
            isRepairAuditForTM={isRepairAuditForTM}
            setIsOperationAuditForDNSD={setIsOperationAuditForDNSD}
            setIsOperationAuditForTM={setIsOperationAuditForTM}
            setIsRepairAuditForDNSD={setIsRepairAuditForDNSD}
            setIsRepairAuditForTM={setIsRepairAuditForTM}
            touched={touched}
            setFieldError={setFieldError}
            additionalEmailError={additionalEmailError}
            setAdditionalEmailError={setAdditionalEmailError}
            setImageUrl={setImageUrl}
            setCountryCodeValue={setCountryCodeValue}
            countryCodeValue={countryCodeValue}
            handleBlur={handleBlur}
            validateField={validateField}
            setFieldTouched={setFieldTouched}
            selectedProductAndSubProductOptionsDNSD={
              selectedProductAndSubProductOptionsDNSD
            }
            selectedProductAndSubProductOptionsTM={
              selectedProductAndSubProductOptionsTM
            }
            imageId={imageUrl}
            isEditMode={isEditMode}
          />
        }
        topSectionStyle={AddUserManagementFormContainerStyle.topSectionStyle}
        bottomSectionStyle={
          AddUserManagementFormContainerStyle.bottomSectionStyle
        }
        bottomSection={
          <Space size={0}>
            <Button
              className={Style["cancel-btn"]}
              ghost
              icon={null}
              onClick={handleCancel}
            >
              {getLocalizedMessage(intl, "button.text.cancel")}
            </Button>
            <Button
              className={Style["Add-Product-Category"]}
              icon={null}
              onClick={handleSubmit}
              disabled={disabled}
            >
              {isEditMode
                ? getLocalizedMessage(intl, "button.text.editUserManagement")
                : getLocalizedMessage(intl, "button.text.addUserManagement")}
            </Button>
          </Space>
        }
      />
      <ConfirmModalContainer
        open={openConfirmPopup}
        setOpen={setOpenConfirmPopup}
        question={getLocalizedMessage(intl, "addUser.text.confirmPopup")}
        iconSrc={"/icons/warningIcon.svg"}
        onConfirm={() => addUserHandler(createUserData)}
      />
    </>
  );
}
